// src/api.js
import axios from 'axios';
import { resolveTenantHint } from './utils/tenant';
/* ------------------------------ API base URL ------------------------------- */
const API_BASE =
  (typeof process !== 'undefined' &&
    process.env &&
    (process.env.REACT_APP_API_BASE || process.env.REACT_APP_API_BASE_URL)) ||
  '/api';

/* --------------------- Storage helper (local or session) --------------------- */
const storage = {
  _whichStore() {
    if (typeof window === 'undefined') return localStorage;
    const pref = localStorage.getItem('authStorage');
    if (pref === 'session') return sessionStorage;
    if (pref === 'local') return localStorage;
    if (localStorage.getItem('refreshToken') || localStorage.getItem('refresh_token')) return localStorage;
    if (sessionStorage.getItem('refreshToken') || sessionStorage.getItem('refresh_token')) return sessionStorage;
    return localStorage; // default
  },
  get access() {
    try {
      return (
        localStorage.getItem('accessToken') ||
        sessionStorage.getItem('accessToken') ||
        localStorage.getItem('token') ||          // backward compat
        sessionStorage.getItem('token') || ''
      );
    } catch { return ''; }
  },
  set access(v) {
    const store = this._whichStore();
    if (!v) {
      try {
        for (const k of ['accessToken','token']) {
          localStorage.removeItem(k); sessionStorage.removeItem(k);
        }
      } catch {}
      return;
    }
    try {
      store.setItem('accessToken', v);
      store.setItem('token', v); // keep old code paths working
    } catch {}
  },
  get refresh() {
    try {
      return (
        localStorage.getItem('refreshToken') ||
        sessionStorage.getItem('refreshToken') ||
        localStorage.getItem('refresh_token') ||
        sessionStorage.getItem('refresh_token') ||
        ''
      );
    } catch { return ''; }
  },
  set refresh(v) {
    const store = this._whichStore();
    try {
      if (v) {
        store.setItem('refreshToken', v);
        store.setItem('refresh_token', v); // keep both shapes aligned
      } else {
        for (const k of ['refreshToken','refresh_token']) {
          localStorage.removeItem(k); sessionStorage.removeItem(k);
        }
      }
    } catch {}
  },
  clear() {
    try {
      for (const k of ['accessToken','token','refreshToken','refresh_token','user']) {
        localStorage.removeItem(k); sessionStorage.removeItem(k);
      }
      localStorage.removeItem('authStorage');
    } catch {}
  }
};

/* ---------------- JWT exp decode + proactive refresh scheduler --------------- */
function jwtExpMs(token) {
  try {
    const b64 = token.split('.')[1];
    if (!b64) return null;
    const json = JSON.parse(atob(b64.replace(/-/g, '+').replace(/_/g, '/')));
    if (json?.exp) return json.exp * 1000;
  } catch {}
  return null;
}

let refreshTimer = null;
function stopProactiveRefresh() {
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = null;
}

function startProactiveRefresh({ skewMs = 2 * 60 * 1000, minDelayMs = 10_000 } = {}) {
  stopProactiveRefresh();
  const token = storage.access;
  if (!token) return;

  const expMs = jwtExpMs(token);
  const fallbackDelay = 30 * 60 * 1000 - skewMs; // assumes 30m if unknown

  const delay = expMs
    ? Math.max(minDelayMs, expMs - Date.now() - skewMs)
    : Math.max(minDelayMs, fallbackDelay);

  refreshTimer = setTimeout(async () => {
    try {
      await doRefresh();
      // schedule next cycle off the NEW token
      startProactiveRefresh({ skewMs, minDelayMs });
    } catch {
      stopProactiveRefresh();
    }
  }, delay);
}

/* -------------------------- Multi-tab token syncing -------------------------- */
const bc = (typeof window !== 'undefined' && 'BroadcastChannel' in window)
  ? new BroadcastChannel('auth')
  : null;

function broadcast(msg) { try { bc?.postMessage(msg); } catch {} }

if (bc) {
  bc.onmessage = (e) => {
    const { type, accessToken, refreshToken, user } = e.data || {};
    if (type === 'tokens') {
      if (accessToken) storage.access = accessToken;
      if (refreshToken) storage.refresh = refreshToken;
      try { if (user) storage._whichStore().setItem('user', JSON.stringify(user)); } catch {}
      startProactiveRefresh();
    } else if (type === 'logout') {
      storage.clear();
      stopProactiveRefresh();
    }
  };
}

/* -------------------------------- Axios clients ------------------------------ */
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 300000,
});

api.interceptors.request.use((cfg) => {
  // Auth header
  const t = storage.access;
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };

  // Tenant header (ALWAYS add)
  const tenantHint = cfg.headers?.['X-Tenant-ID'] || cfg.headers?.['x-tenant-id'] || resolveTenantHint();
  if (tenantHint) cfg.headers = { ...(cfg.headers || {}), 'X-Tenant-ID': tenantHint };

  return cfg;
});

/** Clean client for /auth/refresh (never send Authorization) but still adds tenant */
const refreshClient = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
});
refreshClient.interceptors.request.use((cfg) => {
  const tenantHint = resolveTenantHint();
  if (tenantHint) cfg.headers = { ...(cfg.headers || {}), 'X-Tenant-ID': tenantHint };
  if (cfg.headers?.Authorization) delete cfg.headers.Authorization;
  return cfg;
});

/* --------------------------------- Refresh flow ------------------------------ */
let refreshPromise = null;
async function doRefresh() {
  if (!refreshPromise) {
    const r = storage.refresh;
    if (!r) throw new Error('No refresh token');

    // Backend-agnostic body: send camelCase + snake_case
    const body = { refreshToken: r, refresh_token: r };

    refreshPromise = refreshClient.post('/auth/refresh', body)
      .then(({ data }) => {
        const at = data?.accessToken ?? data?.token ?? data?.access_token;
        const rt = data?.refreshToken ?? data?.refresh_token ?? r; // keep old if not rotated
        if (!at) throw new Error('Bad refresh response: no access token');

        storage.access = at;
        storage.refresh = rt;

        if (data?.user) {
          try { storage._whichStore().setItem('user', JSON.stringify(data.user)); } catch {}
        }

        // reschedule proactively after any successful refresh
        startProactiveRefresh();

        broadcast({ type: 'tokens', accessToken: at, refreshToken: rt, user: data?.user || null });
        return at;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

/* ------------------------------ 401 retry wiring ----------------------------- */
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const { config, response } = error || {};
    if (!response) throw error;
    if (response.status !== 401) throw error;
    if (config?.__isRetry) throw error;
    if (config?.url?.includes('/auth/login') || config?.url?.includes('/auth/refresh')) throw error;

    try {
      await doRefresh();
      const retryCfg = {
        ...config,
        __isRetry: true,
        headers: { ...(config.headers || {}), Authorization: `Bearer ${storage.access}` }
      };
      // Interceptor will add X-Tenant-ID again
      return api.request(retryCfg);
    } catch {
      storage.clear();
      stopProactiveRefresh();
      broadcast({ type: 'logout' });
      throw error;
    }
  }
);

/* ------------------------------ Public helpers ------------------------------- */
export function setTokens(arg) {
  // Accept both shapes: setTokens({ accessToken, refreshToken, user }) OR setTokens(access, refresh)
  let at = '', rt = '', user = null;
  if (typeof arg === 'object' && arg !== null) {
    at = arg.accessToken || arg.token || arg.access_token || '';
    rt = arg.refreshToken || arg.refresh_token || '';
    user = arg.user || null;
  } else {
    // legacy: setTokens(access, refresh)
    at = arguments[0] || '';
    rt = arguments[1] || '';
  }

  if (at) storage.access = at;
  if (rt) storage.refresh = rt;
  try { if (user) storage._whichStore().setItem('user', JSON.stringify(user)); } catch {}

  startProactiveRefresh();
  broadcast({ type: 'tokens', accessToken: at, refreshToken: rt, user: user || null });
}

export function setAuthStorage(pref) {
  try {
    if (pref === 'session' || pref === 'local') {
      localStorage.setItem('authStorage', pref);
    } else {
      localStorage.removeItem('authStorage');
    }
  } catch {}
}

export function clearTokens() {
  storage.clear();
  stopProactiveRefresh();
  broadcast({ type: 'logout' });
}

export async function performLogout(redirect = true) {
  const r =
    localStorage.getItem('refreshToken') ||
    sessionStorage.getItem('refreshToken') ||
    localStorage.getItem('refresh_token') ||
    sessionStorage.getItem('refresh_token');
  try { if (r) await api.post('/auth/logout', { refreshToken: r, refresh_token: r }); } catch {}
  clearTokens();
  if (redirect) window.location.assign('/login');
}

/* ------------------------------ Bootstrap on load ---------------------------- */
// Start proactive refresh if tokens exist when the app boots (hard reload case)
if (storage.access && storage.refresh) {
  startProactiveRefresh();
}

export default api;
